import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Grid,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Fab,
  Tooltip,
  Link
} from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { SeriesLineOptions, SeriesColumnOptions, SeriesPieOptions, SeriesBarOptions } from 'highcharts';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import BackArrow from '../../assets/images/BackArrow.svg';
import { getNegotiationSpendData, getNegotiationTenderSummary, getNegotiationRedFlags, getSupplierBidData, getHistoricalData, getMarketPriceData } from '../../api/negotiations';
import Sidebar from '../../components/Sidebar';
import CitationPanel from '../../components/citationPanel/CitationPanel';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`negotiation-tabpanel-${index}`}
      aria-labelledby={`negotiation-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `negotiation-tab-${index}`,
    'aria-controls': `negotiation-tabpanel-${index}`,
  };
}

// Define interface for spend data items
interface SpendDataItem {
  Supplier: string;
  Main_Category: string;
  'Product Name': string;
  Category: string;
  'Packaging Size': string;
  'Quantity - 2021': number;
  'Price (EUR) - 2021': number;
  'Total (EUR) - 2021': number;
  'Quantity - 2022': number;
  'Price (EUR) - 2022': number;
  'Total (EUR) - 2022': number;
  'Quantity - 2023': number;
  'Price (EUR) - 2023': number;
  'Total (EUR) - 2023': number;
  'Quantity (expected) - 2024': number;
  'Price (EUR) - 2024': number;
  'Total (EUR) - expected - 2024': number;
}

// Define interface for tender summary items
interface TenderSummaryItem {
  [key: string]: any;
  file_id: number;
  file_name: string;
  contract_workspace: string;
  summary: string;
  supplier_name: string;
}

// Define interface for red flags items
interface RedFlagItem {
  [key: string]: any;
  'Supplier name': string;
  'Criteria': string;
  'Description': string;
  'Citation': string;
  file_id: number;
}

// Define interface for supplier bid data items
interface SupplierBidItem {
  [key: string]: any;
  'Supplier Name': string;
  'Product Name': string;
  'unit_price': number;
  'avg_unit_consumed': number;
  'total_units': number;
  'to_kg_multiple': number;
}

const Negotiations: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [spendData, setSpendData] = useState<SpendDataItem[]>([]);
  const [tenderData, setTenderData] = useState<TenderSummaryItem[]>([]);
  const [redFlagsData, setRedFlagsData] = useState<RedFlagItem[]>([]);
  const [supplierBidData, setSupplierBidData] = useState<SupplierBidItem[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCitationOpen, setIsCitationOpen] = useState(false);
  const [selectedReference, setSelectedReference] = useState<{id: number; fileName: string; pageNumber: number; fileId: string; text: string} | null>(null);
  const [chartOptions, setChartOptions] = useState<{[key: string]: Highcharts.Options}>({});
  const [bidChartOptions, setBidChartOptions] = useState<{[key: string]: Highcharts.Options}>({});
  const [pieChartOptions, setPieChartOptions] = useState<{[key: string]: Highcharts.Options}>({});
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [allSuppliers, setAllSuppliers] = useState<string[]>([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [allProducts, setAllProducts] = useState<string[]>([]);
  const [loadingTender, setLoadingTender] = useState(false);
  const [loadingRedFlags, setLoadingRedFlags] = useState(false);
  const [loadingBids, setLoadingBids] = useState(false);
  const [reasoningData, setReasoningData] = useState<string>("");
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({});
  const [selectedSupplierTab, setSelectedSupplierTab] = useState(0);
  const [historicalData, setHistoricalData] = useState<any[]>([]);
  const [marketPriceData, setMarketPriceData] = useState<any[]>([]);
  const [loadingHistoricalData, setLoadingHistoricalData] = useState(false);
  const [loadingMarketPriceData, setLoadingMarketPriceData] = useState(false);
  const [historicalChartOptions, setHistoricalChartOptions] = useState<{[key: string]: Highcharts.Options}>({});
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const toggleSection = (sectionTitle: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle]
    }));
  };

  // Product selection is now handled directly in the button click handlers

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedSuppliers([]);
  };



  // Function to extract all unique categories from data
  const extractCategories = (data: SpendDataItem[]): string[] => {
    if (!Array.isArray(data)) return [];
    
    const categories = new Set<string>();
    data.forEach(item => {
      if (item && item['Category']) {
        categories.add(item['Category']);
      }
    });
    return Array.from(categories).sort();
  };

  // Function to extract all unique suppliers from data
  const extractSuppliers = (data: SpendDataItem[]): string[] => {
    if (!Array.isArray(data)) return [];
    
    const suppliers = new Set<string>();
    data.forEach(item => {
      if (item && item['Supplier']) {
        suppliers.add(item['Supplier']);
      }
    });
    
    // Add CheeseKing as a challenger supplier (no historic spend data)
    suppliers.add('CheeseKing');
    
    return Array.from(suppliers).sort();
  };

  // Function to prepare chart data by supplier
  const prepareChartData = (data: SpendDataItem[], categoryFilter: string[] = [], supplierFilter: string[] = []) => {
    if (!Array.isArray(data) || data.length === 0) {
      return {};
    }

    const chartOptionsMap: {[key: string]: Highcharts.Options} = {};
    
    // Group data by supplier
    const supplierGroups: {[key: string]: SpendDataItem[]} = {};
    data.forEach(item => {
      const supplier = item.Supplier;
      if (!supplierGroups[supplier]) {
        supplierGroups[supplier] = [];
      }
      supplierGroups[supplier].push(item);
    });

    // Filter suppliers if needed
    let suppliersToProcess = Object.keys(supplierGroups);
    if (supplierFilter.length > 0) {
      suppliersToProcess = suppliersToProcess.filter(supplier => supplierFilter.includes(supplier));
    }

    // For each supplier, create a chart
    suppliersToProcess.forEach(supplier => {
      const supplierItems = supplierGroups[supplier];
      // Start with all supplier items
      let filteredData = [...supplierItems];
      
      // Apply category filter if selected
      if (categoryFilter.length > 0) {
        filteredData = filteredData.filter(item => categoryFilter.includes(item['Category']));
      }
      
      // Group by category and sum the totals for each year
      const categoryTotals: {[key: string]: {[year: string]: number}} = {};
      
      filteredData.forEach(item => {
        const category = item['Category'];
        if (!categoryTotals[category]) {
          categoryTotals[category] = {
            '2021': 0,
            '2022': 0,
            '2023': 0,
            '2024': 0
          };
        }
        
        // Add totals for each year
        if (item['Total (EUR) - 2021']) {
          categoryTotals[category]['2021'] += parseFloat(String(item['Total (EUR) - 2021']));
        }
        if (item['Total (EUR) - 2022']) {
          categoryTotals[category]['2022'] += parseFloat(String(item['Total (EUR) - 2022']));
        }
        if (item['Total (EUR) - 2023']) {
          categoryTotals[category]['2023'] += parseFloat(String(item['Total (EUR) - 2023']));
        }
        if (item['Total (EUR) - expected - 2024']) {
          categoryTotals[category]['2024'] += parseFloat(String(item['Total (EUR) - expected - 2024']));
        }
      });
      
      // Create series for the chart
      const series: SeriesLineOptions[] = Object.entries(categoryTotals).map(([category, yearTotals]) => ({
        type: 'line',
        name: category,
        data: [yearTotals['2021'] || 0, yearTotals['2022'] || 0, yearTotals['2023'] || 0, yearTotals['2024'] || 0]
      }));
      
      // Create chart options
      chartOptionsMap[supplier] = {
        chart: {
          type: 'line',
          height: 300
        },
        title: {
          text: `${supplier} - Category Spend Trends`
        },
        xAxis: {
          categories: ['2021', '2022', '2023', '2024'],
          title: {
            text: 'Year'
          }
        },
        yAxis: {
          title: {
            text: 'Total Spend (EUR)'
          }
        },
        tooltip: {
          headerFormat: '<b>{series.name}</b><br/>',
          pointFormat: '{point.category}: €{point.y:,.2f}'
        },
        plotOptions: {
          line: {
            marker: {
              enabled: true
            }
          }
        },
        series: series,
        credits: {
          enabled: false
        }
      };
    });
    
    return chartOptionsMap;
  };
  
  // Update charts when filters change
  useEffect(() => {
    if (spendData && spendData.length > 0) {
      try {
        // Update line charts
        const chartOptionsMap = prepareChartData(spendData, selectedCategories, selectedSuppliers);
        setChartOptions(chartOptionsMap);
        
        // Update pie charts
        const pieChartOptionsMap = prepareSpendPieChartData(spendData, selectedCategories, selectedSuppliers);
        setPieChartOptions(pieChartOptionsMap);
      } catch (error) {
        console.error('Error updating charts with filters:', error);
      }
    }
  }, [selectedCategories, selectedSuppliers, spendData]);

  // Fetch spend data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const contractId = 78;
        const data = await getNegotiationSpendData(contractId);
        
        if (Array.isArray(data) && data.length > 0) {
          setSpendData(data);
        } else {
          console.error('Invalid data format received:', data);
          setSpendData([]);
        }
        
        // Extract all categories and suppliers
        const categories = extractCategories(data);
        setAllCategories(categories);
        
        const suppliers = extractSuppliers(data);
        setAllSuppliers(suppliers);
        
        // Prepare chart data
        const chartOptionsMap = prepareChartData(data);
        setChartOptions(chartOptionsMap);
        
        // Prepare pie chart data for 2024 spend volume
        const pieChartOptionsMap = prepareSpendPieChartData(data);
        setPieChartOptions(pieChartOptionsMap);

        // Fetch red flags data
        setLoadingRedFlags(true);
        try {
          const redFlagsData = await getNegotiationRedFlags();
          if (Array.isArray(redFlagsData) && redFlagsData.length > 0) {
            setRedFlagsData(redFlagsData);
          } else {
            console.error('Invalid red flags data format received:', redFlagsData);
            setRedFlagsData([]);
          }
        } catch (error) {
          console.error('Error fetching red flags data:', error);
          setRedFlagsData([]);
        } finally {
          setLoadingRedFlags(false);
        }
      } catch (error) {
        console.error('Error fetching spend data:', error);
        setSpendData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Function to prepare pie chart data for 2024 spend volume by supplier and category
  const prepareSpendPieChartData = (data: SpendDataItem[], categoryFilter: string[] = [], supplierFilter: string[] = []) => {
    if (!Array.isArray(data) || data.length === 0) {
      return {};
    }
    
    // We'll create two pie charts:
    // 1. Spend by Supplier
    // 2. Spend by Category
    
    // Filter the data based on the selected filters
    let filteredData = [...data];
    
    // Apply category filter if selected
    if (categoryFilter.length > 0) {
      filteredData = filteredData.filter(item => categoryFilter.includes(item['Category']));
    }
    
    // Apply supplier filter if selected
    if (supplierFilter.length > 0) {
      filteredData = filteredData.filter(item => supplierFilter.includes(item['Supplier']));
    }
    
    // For each chart, we need to aggregate the data
    const pieChartOptionsMap: {[key: string]: Highcharts.Options} = {};
    
    // 1. Spend by Supplier
    const supplierSpend: {[key: string]: number} = {};
    filteredData.forEach(item => {
      const supplier = item.Supplier;
      const spend = item['Total (EUR) - expected - 2024'] || 0;
      
      if (!supplierSpend[supplier]) {
        supplierSpend[supplier] = 0;
      }
      supplierSpend[supplier] += spend;
    });
    
    // Create data for the chart
    const supplierData = Object.entries(supplierSpend).map(([supplier, spend]) => ({
      name: supplier,
      y: spend
    }));
    
    // Create chart options
    pieChartOptionsMap['supplier'] = {
      chart: {
        type: 'pie',
        height: 300
      },
      title: {
        text: 'Spend by Supplier (2024)'
      },
      tooltip: {
        pointFormat: '{series.name}: <b>€{point.y:,.2f}</b>'
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f}%'
          }
        }
      },
      series: [{
        type: 'pie',
        name: 'Spend',
        data: supplierData
      } as SeriesPieOptions],
      credits: {
        enabled: false
      }
    };
    
    // 2. Spend by Category
    const categorySpend: {[key: string]: number} = {};
    filteredData.forEach(item => {
      const category = item.Category;
      const spend = item['Total (EUR) - expected - 2024'] || 0;
      
      if (!categorySpend[category]) {
        categorySpend[category] = 0;
      }
      categorySpend[category] += spend;
    });
    
    // Create data for the chart
    const categoryData = Object.entries(categorySpend).map(([category, spend]) => ({
      name: category,
      y: spend
    }));
    
    // Create chart options
    pieChartOptionsMap['category'] = {
      chart: {
        type: 'pie',
        height: 300
      },
      title: {
        text: 'Spend by Category (2024)'
      },
      tooltip: {
        pointFormat: '{series.name}: <b>€{point.y:,.2f}</b>'
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f}%'
          }
        }
      },
      series: [{
        type: 'pie',
        name: 'Spend',
        data: categoryData
      } as SeriesPieOptions],
      credits: {
        enabled: false
      }
    };
    
    return pieChartOptionsMap;
  };
  
  // Function to prepare bar chart data for supplier bids
  const prepareBidChartData = (data: SupplierBidItem[]) => {
    if (!Array.isArray(data) || data.length === 0) {
      return {};
    }

    // Group data by product name
    const productGroups: {[key: string]: SupplierBidItem[]} = {};
    data.forEach(item => {
      const product = item['Product Name'];
      if (!productGroups[product]) {
        productGroups[product] = [];
      }
      productGroups[product].push(item);
    });

    // Extract all unique products
    const products = Object.keys(productGroups);
    setAllProducts(products);

    // For each product, create a horizontal bar chart
    const bidChartOptionsMap: {[key: string]: Highcharts.Options} = {};

    products.forEach(product => {
      const productItems = productGroups[product];
      
      // Find the baseline item for this product
      const baselineItem = productItems.find(item => item['Supplier Name'] === 'baseline');
      const baselinePrice = baselineItem ? baselineItem.unit_price : 0;
      
      // Prepare data for the chart - use actual prices instead of differences
      const suppliers: string[] = [];
      const actualPrices: number[] = [];
      const priceDifferences: number[] = [];
      const colors: string[] = [];
      
      // Add all suppliers except baseline
      productItems
        .filter(item => item['Supplier Name'] !== 'baseline')
        .forEach(item => {
          suppliers.push(item['Supplier Name']);
          actualPrices.push(item.unit_price);
          const difference = item.unit_price - baselinePrice;
          priceDifferences.push(difference);
          // Color bars based on comparison to baseline
          colors.push(difference <= 0 ? '#4CAF50' : '#F44336');
        });
      
      // Create series for the chart
      const series = [{
        type: 'bar' as const,
        name: 'Unit Price',
        data: actualPrices.map((price, index) => ({
          y: price,
          color: colors[index],
          custom: {
            difference: priceDifferences[index]
          }
        })),
        colorByPoint: true
      }];
      
      // Find the min and max prices to set appropriate axis limits
      const minPrice = Math.min(...actualPrices, baselinePrice);
      const maxPrice = Math.max(...actualPrices, baselinePrice);
      // Calculate a good range that will show small differences clearly
      const priceRange = maxPrice - minPrice;
      const buffer = Math.max(priceRange * 0.1, 0.001); // At least 0.001 buffer
      const yMin = Math.max(0, minPrice - buffer); // Don't go below zero unless prices are negative
      const yMax = maxPrice + buffer;
      
      // Create chart options
      bidChartOptionsMap[product] = {
        chart: {
          type: 'bar' as const,
          height: 300 // Reduced height
        },
        title: {
          text: `${product} - Price Comparison vs Baseline (€${baselinePrice.toFixed(3)})`
        },
        xAxis: {
          categories: suppliers,
          title: {
            text: 'Supplier'
          }
        },
        yAxis: {
          title: {
            text: 'Unit Price (EUR)'
          },
          labels: {
            format: '{value:.3f}' // Show 3 decimal places
          },
          plotLines: [{
            value: baselinePrice,
            color: '#000',
            width: 2,
            zIndex: 5,
            label: {
              text: `Baseline: €${baselinePrice.toFixed(3)}`,
              align: 'right',
              verticalAlign: 'bottom',
              y: 15, // Move label down below the line
              style: {
                color: '#000',
                fontWeight: 'bold'
              }
            }
          }],
          // Set min and max to ensure small differences are visible
          min: yMin,
          max: yMax,
          tickInterval: priceRange > 0.05 ? priceRange / 5 : 0.01 // More granular ticks
        },
        tooltip: {
          headerFormat: '<b>{point.x}</b><br/>',
          pointFormatter: function() {
            // @ts-ignore - custom property for difference
            const diff = this.custom?.difference || 0;
            const price = this.y || 0;
            // Calculate percentage difference
            const percentDiff = baselinePrice !== 0 ? (diff / baselinePrice) * 100 : 0;
            const percentFormatted = percentDiff >= 0 ? `+${percentDiff.toFixed(2)}%` : `${percentDiff.toFixed(2)}%`;
            return `Price: €${price.toFixed(3)}<br/>` +
                   `vs Baseline: ${percentFormatted} (${diff >= 0 ? 'higher' : 'lower'})<br/>` +
                   `Baseline: €${baselinePrice.toFixed(3)}`;
          }
        },
        plotOptions: {
          bar: {
            pointPadding: 0.2,
            borderWidth: 0,
            dataLabels: {
              enabled: true,
              formatter: function() {
                // @ts-ignore - custom property for difference
                const diff = this.point.custom?.difference || 0;
                // Calculate percentage difference
                const percentDiff = baselinePrice !== 0 ? (diff / baselinePrice) * 100 : 0;
                const percentFormatted = percentDiff >= 0 ? `+${percentDiff.toFixed(2)}%` : `${percentDiff.toFixed(2)}%`;
                return percentFormatted;
              },
              style: {
                fontWeight: 'bold',
                color: 'black'
              }
            }
          }
        },
        series: series,
        credits: {
          enabled: false
        }
      };
    });
    
    return bidChartOptionsMap;
  };

  // Load reasoning data from data.txt
  useEffect(() => {
    const fetchReasoningData = async () => {
      try {
        const response = await fetch('/src/pages/negotiations/data.txt');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        setReasoningData(text);
        
        // Initialize all sections as collapsed
        const sectionRegex = /^\d+\. ([^\n]+)/gm;
        let match;
        const initialExpandedState: {[key: string]: boolean} = {};
        
        while ((match = sectionRegex.exec(text)) !== null) {
          const sectionTitle = match[0];
          initialExpandedState[sectionTitle] = false; // Set all sections to collapsed by default
        }
        
        setExpandedSections(initialExpandedState);
      } catch (error) {
        console.error('Error loading reasoning data:', error);
        setReasoningData('Error loading reasoning data. Please try again later.');
      }
    };

    fetchReasoningData();
  }, []);

  // Function to prepare historical data charts for should-cost modeling with market price comparison
  const prepareHistoricalDataCharts = (historicalData: any[], marketPriceData: any[]) => {
    if (!Array.isArray(historicalData) || historicalData.length === 0) {
      return {};
    }
    
    // Group historical data by category
    const categoryGroups: {[key: string]: any[]} = {};
    historicalData.forEach(item => {
      const category = item['Product Category'] || 'Uncategorized';
      if (!categoryGroups[category]) {
        categoryGroups[category] = [];
      }
      categoryGroups[category].push(item);
    });
    
    // Group market price data by category
    const marketPriceByCategory: {[key: string]: any[]} = {};
    if (Array.isArray(marketPriceData) && marketPriceData.length > 0) {
      marketPriceData.forEach(item => {
        const category = item['Product Category'] || 'Uncategorized';
        if (!marketPriceByCategory[category]) {
          marketPriceByCategory[category] = [];
        }
        marketPriceByCategory[category].push(item);
      });
    }
    
    // Create chart options for each category
    const chartOptionsMap: {[key: string]: Highcharts.Options} = {};
    
    // Process each category
    Object.keys(categoryGroups).forEach(category => {
      const categoryData = categoryGroups[category];
      
      // Get unique years and suppliers for this category
      const years = Array.from(new Set(categoryData.map(item => item.Year))).sort();
      const suppliers = Array.from(new Set(categoryData.map(item => item['Supplier Name'])));
      
      // Prepare series data for each supplier
      const series: (SeriesColumnOptions | SeriesLineOptions)[] = suppliers.map(supplier => {
        const supplierData = years.map(year => {
          const matchingItems = categoryData.filter(item => 
            item['Supplier Name'] === supplier && item.Year === year
          );
          
          // If multiple items found for the same supplier and year, use the average price
          if (matchingItems.length > 0) {
            const totalPrice = matchingItems.reduce((sum, item) => sum + (item['Price (EUR)'] || 0), 0);
            return totalPrice / matchingItems.length;
          }
          return null; // No data for this supplier and year
        });
        
        return {
          type: 'column',
          name: supplier,
          data: supplierData
        };
      });
      
      // Add market price data as a line series if available for this category
      if (marketPriceByCategory[category]) {
        // Process market price data to match the years in historical data
        const marketPriceData = marketPriceByCategory[category];
        
        // Group market price data by year (using the year from the Date field)
        const marketPriceByYear: {[key: string]: number[]} = {};
        marketPriceData.forEach(item => {
          const date = new Date(item.Date);
          const year = date.getFullYear().toString();
          if (!marketPriceByYear[year]) {
            marketPriceByYear[year] = [];
          }
          // Use Unit_Price instead of Price
          marketPriceByYear[year].push(item.Unit_Price);
        });
        
        // Calculate average market price for each year
        const marketPriceSeries: number[] = years.map(year => {
          const yearStr = year.toString();
          if (marketPriceByYear[yearStr] && marketPriceByYear[yearStr].length > 0) {
            const sum = marketPriceByYear[yearStr].reduce((a, b) => a + b, 0);
            return sum / marketPriceByYear[yearStr].length;
          }
          return 0; // Default to 0 when no market price data is available for this year
        });
        
        // Add market price series
        series.push({
          type: 'line',
          name: 'Market Price Index',
          data: marketPriceSeries as any, // Cast to any to avoid TypeScript error with null values
          color: '#FF0000', // Red color for market price line
          lineWidth: 3,
          marker: {
            symbol: 'circle',
            radius: 5
          },
          zIndex: 100, // Ensure line appears above columns
          tooltip: {
            valueDecimals: 2,
            valueSuffix: ' EUR'
          }
        });
      }
      
      // Create chart options
      chartOptionsMap[category] = {
        chart: {
          type: 'column',
          height: 400
        },
        title: {
          text: `${category} - Price vs Market Price Comparison`
        },
        xAxis: {
          categories: years.map(year => year.toString()),
          title: {
            text: 'Year'
          },
          crosshair: true
        },
        yAxis: {
          title: {
            text: 'Price (EUR)'
          },
          min: 0
        },
        tooltip: {
          headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
          pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
            '<td style="padding:0"><b>€{point.y:.3f}</b></td></tr>',
          footerFormat: '</table>',
          shared: true,
          useHTML: true
        },
        plotOptions: {
          column: {
            pointPadding: 0.2,
            borderWidth: 0,
            groupPadding: 0.1
          },
          line: {
            marker: {
              enabled: true
            }
          }
        },
        series: series,
        credits: {
          enabled: false
        },
        legend: {
          enabled: true,
          itemStyle: {
            fontWeight: 'normal'
          },
          itemHoverStyle: {
            fontWeight: 'bold'
          }
        }
      };
    });
    
    return chartOptionsMap;
  };
  
  // Fetch historical data and market price data for should-cost modeling
  useEffect(() => {
    const fetchShouldCostData = async () => {
      if (tabValue === 2) { // Only fetch when Should-Cost tab is selected
        setLoadingHistoricalData(true);
        setLoadingMarketPriceData(true);
        
        try {
          // Fetch historical data
          const historicalDataResponse = await getHistoricalData();
          let historicalDataArray: any[] = [];
          
          if (Array.isArray(historicalDataResponse) && historicalDataResponse.length > 0) {
            historicalDataArray = historicalDataResponse;
            setHistoricalData(historicalDataArray);
          } else {
            console.error('Invalid historical data format received:', historicalDataResponse);
            setHistoricalData([]);
          }
          
          // Fetch market price data
          const marketPriceResponse = await getMarketPriceData();
          let marketPriceArray: any[] = [];
          
          if (Array.isArray(marketPriceResponse) && marketPriceResponse.length > 0) {
            marketPriceArray = marketPriceResponse;
            setMarketPriceData(marketPriceArray);
          } else {
            console.error('Invalid market price data format received:', marketPriceResponse);
            setMarketPriceData([]);
          }
          
          // Prepare chart data with both historical and market price data
          const chartOptions = prepareHistoricalDataCharts(historicalDataArray, marketPriceArray);
          setHistoricalChartOptions(chartOptions);
          
        } catch (error) {
          console.error('Error fetching should-cost data:', error);
          setHistoricalData([]);
          setMarketPriceData([]);
          setHistoricalChartOptions({});
        } finally {
          setLoadingHistoricalData(false);
          setLoadingMarketPriceData(false);
        }
      }
    };
    
    fetchShouldCostData();
  }, [tabValue]);

  // Fetch tender summary data and supplier bid data
  useEffect(() => {
    const fetchTenderData = async () => {
      if (tabValue === 1) { // Only fetch when Tender Summary tab is selected
        setLoadingTender(true);
        setLoadingBids(true);
        try {
          // Fetch tender summary data
          const tenderSummaryData = await getNegotiationTenderSummary();
          
          if (Array.isArray(tenderSummaryData) && tenderSummaryData.length > 0) {
            setTenderData(tenderSummaryData);
          } else {
            console.error('Invalid tender data format received:', tenderSummaryData);
            setTenderData([]);
          }
          
          // Fetch supplier bid data
          const bidData = await getSupplierBidData();
          
          if (Array.isArray(bidData) && bidData.length > 0) {
            setSupplierBidData(bidData);
            
            // Prepare chart data for supplier bids
            const bidChartOptionsMap = prepareBidChartData(bidData);
            setBidChartOptions(bidChartOptionsMap);
          } else {
            console.error('Invalid supplier bid data format received:', bidData);
            setSupplierBidData([]);
            setBidChartOptions({});
          }
        } catch (error) {
          console.error('Error fetching tender data:', error);
          setTenderData([]);
          setSupplierBidData([]);
          setBidChartOptions({});
        } finally {
          setLoadingTender(false);
          setLoadingBids(false);
        }
      }
    };

    fetchTenderData();
  }, [tabValue]);

  const handleOpenSidebar = () => {
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleOpenCitation = () => {
    setSelectedReference({
      id: 1,
      fileName: 'Supplier Cost Data',
      pageNumber: 10,
      fileId: '232',
      text: 'Supplier cost breakdown and analysis'
    });
    setIsCitationOpen(true);
  };
  
  const handleOpenRedFlagCitation = (flag: RedFlagItem) => {
    setSelectedReference({
      id: flag.file_id,
      fileName: flag.Citation,
      pageNumber: 3, // Default to page 0 as specified
      fileId: flag.file_id.toString(),
      text: flag.Description
    });
    setIsCitationOpen(true);
  };

  const handleCloseCitation = () => {
    setIsCitationOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      <Grid
        sx={{
          flex: 1,
          mt: 8,
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
          borderRadius: "8px",
          width: "100%",
          pb: 4
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
          <Button
            onClick={() => navigate('/homepage')}
            sx={{ 
              mr: 2,
              color: '#8950FC',
              textTransform: 'none',
              '&:hover': {
                background: 'transparent',
                color: '#7339FB'
              }
            }}
            startIcon={<img src={BackArrow} alt="Back" />}
          >
            Back
          </Button>
          <Box>
            <Breadcrumbs aria-label="breadcrumb">
              <RouterLink to="/homepage" style={{ textDecoration: 'none', color: 'inherit' }}>
                Home
              </RouterLink>
              <RouterLink to="/homepage/negotiations" style={{ textDecoration: 'none', color: 'inherit' }}>
                Negotiations
              </RouterLink>
              <Typography color="text.primary">Dairy 2025</Typography>
            </Breadcrumbs>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
        </Box>
        <Divider sx={{ width: "100%", mb: 2 }} />
        
        <Box sx={{ px: 3, width: '100%' }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Negotiation Summary - Dairy 2025
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {/* Headline Stats */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'row', 
                    justifyContent: 'space-between',
                    mb: 3,
                    mt: 2,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'rgba(137, 80, 252, 0.08)',
                  }}>
                    {/* Total Products */}
                    <Box sx={{ 
                      flex: 1, 
                      textAlign: 'center',
                      p: 2,
                      borderRight: '1px solid rgba(137, 80, 252, 0.3)'
                    }}>
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        3
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                        Total Products
                      </Typography>
                    </Box>
                    
                    {/* Total Annual Spend */}
                    <Box sx={{ 
                      flex: 1, 
                      textAlign: 'center',
                      p: 2,
                      borderRight: '1px solid rgba(137, 80, 252, 0.3)'
                    }}>
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        €1.2M
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                        Total Annual Spend
                      </Typography>
                    </Box>
                    
                    {/* Total Suppliers */}
                    <Box sx={{ 
                      flex: 1, 
                      textAlign: 'center',
                      p: 2,
                      borderRight: '1px solid rgba(137, 80, 252, 0.3)'
                    }}>
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        4
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                        Total Suppliers
                      </Typography>
                    </Box>
                    
                    {/* Potential Savings */}
                    <Box sx={{ 
                      flex: 1, 
                      textAlign: 'center',
                      p: 2,
                      bgcolor: '#5E35B1',
                      color: 'white'
                    }}>
                      <Typography variant="h4" fontWeight="bold" color="white">
                        €120K
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1rem', color: 'white' }}>
                        Potential Savings
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                    Detailed Summary
                  </Typography>
                  <Box sx={{ pl: 2, mb: 3 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'row', 
                      mb: 2,
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}>
                      {/* Products Column */}
                      <Box sx={{ 
                        flex: 1, 
                        p: 2,
                        borderRight: '1px solid rgba(0, 0, 0, 0.12)'
                      }}>
                        <Typography variant="body1" sx={{ mb: 1, fontSize: '1rem' }}>
                          <strong>Products:</strong>
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0 }}>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Sliced Cheddar – 250g (250,000 units annually)</Typography></Box>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Cottage Cheese 4% – 400g (190,000 units annually)</Typography></Box>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Mascarpone – 250g (65,000+ units annually)</Typography></Box>
                        </Box>
                      </Box>
                      
                      {/* Total Annual Spend Column */}
                      <Box sx={{ 
                        flex: 1, 
                        p: 2,
                        borderRight: '1px solid rgba(0, 0, 0, 0.12)'
                      }}>
                        <Typography variant="body1" sx={{ mb: 1, fontSize: '1rem' }}>
                          <strong>Total Annual Spend:</strong>
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0 }}>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Sliced Cheddar: €620,000</Typography></Box>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Cottage Cheese: €437,000</Typography></Box>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Mascarpone: €143,000</Typography></Box>
                        </Box>
                      </Box>
                      
                      {/* Suppliers Column */}
                      <Box sx={{ 
                        flex: 1,
                        p: 2,
                        borderRight: '1px solid rgba(0, 0, 0, 0.12)'
                      }}>
                        <Typography variant="body1" sx={{ mb: 1, fontSize: '1rem' }}>
                          <strong>Suppliers:</strong>
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0 }}>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Incumbents (3): DAIRYLICIOUS, DairyPro, and PremiumDairy</Typography></Box>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Challenger (1): CheeseKing</Typography></Box>
                        </Box>
                      </Box>
                      
                      {/* Potential Savings Column */}
                      <Box sx={{ 
                        flex: 1,
                        p: 2,
                        bgcolor: 'rgba(94, 53, 177, 0.05)'
                      }}>
                        <Typography variant="body1" sx={{ mb: 1, fontSize: '1rem' }}>
                          <strong>Potential Savings:</strong>
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0 }}>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Sliced Cheddar: €0.25/unit × 140,000 units = €35,000</Typography></Box>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Cottage Cheese: €30,000+ (optimized allocation)</Typography></Box>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Mascarpone: €19,000+ (strategic shifts)</Typography></Box>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>Total: 7-10% of dairy spend (€84K-€120K)</Typography></Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                    Negotiation Strategy Overview
                  </Typography>
                  <Box sx={{ mb: 3, px: 1 }}>
                    <Typography variant="body1" sx={{ fontSize: '1rem', mb: 2 }}>
                      This first-round dairy category negotiation analysis integrates cost-breakdowns, component-level data, volume/spend analysis, compliance certifications, and supplier performance metrics to deliver a balanced recommendation across cost optimization, quality, compliance, and sustainability factors.
                    </Typography>
                    
                    <Typography variant="body1" sx={{ fontSize: '1rem', mb: 2 }}>
                      <strong>Key Findings:</strong>
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                          Cost Structure Analysis
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0 }}>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Current fully loaded costs average ~€2.48/unit vs. potential €2.23/unit</Typography></Box>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Processing and packaging represent ~50% of operational expenses</Typography></Box>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>Supplier margins vary significantly (11-15%) across incumbents</Typography></Box>
                        </Box>
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                          Compliance & Quality
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0 }}>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>CheeseKing and PremiumDairy show strongest compliance records</Typography></Box>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>DAIRYLICIOUS has gaps in salt content and EU sourcing documentation</Typography></Box>
                          <Box component="li"><Typography variant="body1" sx={{ fontSize: '1rem' }}>CheeseKing leads in sustainability (recycled packaging, animal welfare)</Typography></Box>
                        </Box>
                      </Box>
                    </Box>
                    
                    <Typography variant="body1" sx={{ fontSize: '1rem', mb: 1 }}>
                      <strong>Recommended Strategy:</strong> A blended, multi-supplier approach is recommended to mitigate single-supplier risk, leverage competition to drive down costs, and ensure high compliance standards. The proposed mix delivers an estimated 7-10% cost reduction while improving overall supply resilience and sustainability credentials.
                    </Typography>
                  </Box>
                  
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                    Current vs. Recommended Supplier Mix
                  </Typography>
                  <Box sx={{ mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 1, overflow: 'hidden' }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Current Supplier Mix</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Recommended Supplier Mix</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: '#5E35B1' }}>Cost Impact</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>Sliced Cheddar – 250g</TableCell>
                            <TableCell>DAIRYLICIOUS (24%), DairyPro (56%), PremiumDairy (20%)</TableCell>
                            <TableCell>DairyPro (40%), PremiumDairy (30%), CheeseKing (30%)</TableCell>
                            <TableCell><strong>€2.48 → €2.23 per unit<br/>€35,000 annual savings</strong></TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Cottage Cheese 4% – 400g</TableCell>
                            <TableCell>DAIRYLICIOUS (45%), DairyPro (42%), PremiumDairy (13%)</TableCell>
                            <TableCell>DairyPro (35%), PremiumDairy/DAIRYLICIOUS (30%), CheeseKing (35%)</TableCell>
                            <TableCell><strong>€30,000+ annual savings<br/>through cost optimization</strong></TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Mascarpone – 250g</TableCell>
                            <TableCell>DAIRYLICIOUS (23%), DairyPro (38%), PremiumDairy (39%)</TableCell>
                            <TableCell>PremiumDairy (60%), CheeseKing (40%)</TableCell>
                            <TableCell><strong>€19,000+ annual savings<br/>from strategic volume shifts</strong></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            

            

            {/* Negotiation Tactics Table */}
            {/* Supplier-Specific Negotiation Section */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Supplier-Specific Negotiation Strategy
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select a supplier to view tailored negotiation targets and tactics based on their specific performance metrics, compliance records, and cost structures.
                </Typography>
                
                {/* Supplier Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  <Tabs 
                    value={selectedSupplierTab} 
                    onChange={(e, newValue) => setSelectedSupplierTab(newValue)}
                    aria-label="supplier negotiation tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#8950FC',
                      },
                      '& .Mui-selected': {
                        color: '#8950FC !important',
                      },
                    }}
                  >
                    <Tab label="DAIRYLICIOUS" {...a11yProps(0)} />
                    <Tab label="DairyPro" {...a11yProps(1)} />
                    <Tab label="PremiumDairy" {...a11yProps(2)} />
                    <Tab label="CheeseKing" {...a11yProps(3)} />
                  </Tabs>
                </Box>
                
                {/* DAIRYLICIOUS */}
                <TabPanel value={selectedSupplierTab} index={0}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                    DAIRYLICIOUS Negotiation Strategy
                  </Typography>
                  
                  {/* Negotiation Targets Table */}
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Negotiation Targets
                  </Typography>
                  <TableContainer sx={{ mb: 4 }}>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '16%' }}>Negotiation Parameter</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Objective</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Opening Position</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>MDO</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>LAA</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>BATNA</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Price</TableCell>
                          <TableCell>Address high supplier margin (15%)</TableCell>
                          <TableCell>10% reduction based on compliance gaps</TableCell>
                          <TableCell>7% reduction</TableCell>
                          <TableCell>5% reduction</TableCell>
                          <TableCell>Shift volume to CheeseKing/PremiumDairy</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Discount</TableCell>
                          <TableCell>Secure volume-based rebates</TableCell>
                          <TableCell>3% rebate for orders {'>'}50,000 units</TableCell>
                          <TableCell>2% rebate</TableCell>
                          <TableCell>1.5% rebate</TableCell>
                          <TableCell>Maintain current pricing</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Payment Terms</TableCell>
                          <TableCell>Extend payment window</TableCell>
                          <TableCell>Net 60 days</TableCell>
                          <TableCell>Net 45 days</TableCell>
                          <TableCell>Net 30 days</TableCell>
                          <TableCell>Current terms</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Incoterms</TableCell>
                          <TableCell>Shift delivery responsibility</TableCell>
                          <TableCell>DDP (Delivered Duty Paid)</TableCell>
                          <TableCell>DAP (Delivered At Place)</TableCell>
                          <TableCell>FCA (Free Carrier)</TableCell>
                          <TableCell>Current EXW terms</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Negotiation Tactics Table */}
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Negotiation Tactics
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>Negotiation Parameter</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>Issue</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Key Message</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Anticipated Response</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Counter Response</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Concessions</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Priority</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Price</TableCell>
                          <TableCell>Missing compliance data (salt content, EU sourcing)</TableCell>
                          <TableCell>Compliance gaps require price concessions to offset risk</TableCell>
                          <TableCell>Documentation gaps are administrative, not performance issues</TableCell>
                          <TableCell>CheeseKing offers full compliance at 10% lower cost</TableCell>
                          <TableCell>Accept phased compliance improvement plan</TableCell>
                          <TableCell>High</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Discount</TableCell>
                          <TableCell>No volume incentives in current contracts</TableCell>
                          <TableCell>Reduced allocation (from 45% to 30%) requires better terms</TableCell>
                          <TableCell>Lower volume means higher unit costs</TableCell>
                          <TableCell>Offer to maintain current volume with improved compliance</TableCell>
                          <TableCell>Accept smaller rebate with compliance improvements</TableCell>
                          <TableCell>Medium</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Payment Terms</TableCell>
                          <TableCell>Current terms are Net 30</TableCell>
                          <TableCell>Extended terms align with industry standards</TableCell>
                          <TableCell>Extended terms increase financial burden</TableCell>
                          <TableCell>Offer early payment options with small discount</TableCell>
                          <TableCell>Accept Net 45 with volume commitment</TableCell>
                          <TableCell>Low</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>
                
                {/* DairyPro */}
                <TabPanel value={selectedSupplierTab} index={1}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                    DairyPro Negotiation Strategy
                  </Typography>
                  
                  {/* Negotiation Targets Table */}
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Negotiation Targets
                  </Typography>
                  <TableContainer sx={{ mb: 4 }}>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '16%' }}>Negotiation Parameter</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Objective</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Opening Position</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>MDO</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>LAA</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>BATNA</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Price</TableCell>
                          <TableCell>Reduce fully loaded costs by 8%</TableCell>
                          <TableCell>8% reduction based on operational cost analysis</TableCell>
                          <TableCell>6% reduction</TableCell>
                          <TableCell>4% reduction</TableCell>
                          <TableCell>Maintain current allocation</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Discount</TableCell>
                          <TableCell>Secure volume-based rebates for Sliced Cheddar</TableCell>
                          <TableCell>3% volume discount for 40-50% allocation</TableCell>
                          <TableCell>2% volume discount</TableCell>
                          <TableCell>1.5% volume discount</TableCell>
                          <TableCell>Tiered pricing structure</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Payment Terms</TableCell>
                          <TableCell>Extend payment window</TableCell>
                          <TableCell>Net 60 days</TableCell>
                          <TableCell>Net 45 days</TableCell>
                          <TableCell>Net 30 days</TableCell>
                          <TableCell>Current terms</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Incoterms</TableCell>
                          <TableCell>Shift delivery responsibility</TableCell>
                          <TableCell>DDP (Delivered Duty Paid)</TableCell>
                          <TableCell>DAP (Delivered At Place)</TableCell>
                          <TableCell>FCA (Free Carrier)</TableCell>
                          <TableCell>Current terms</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Negotiation Tactics Table */}
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Negotiation Tactics
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>Negotiation Parameter</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>Issue</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Key Message</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Anticipated Response</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Counter Response</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Concessions</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Priority</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Price</TableCell>
                          <TableCell>Supplier margin (13%) is above market average</TableCell>
                          <TableCell>Benchmarking shows competitors offer lower margins</TableCell>
                          <TableCell>Higher margin reflects quality and reliability</TableCell>
                          <TableCell>Maintaining 40-50% volume allocation justifies margin reduction</TableCell>
                          <TableCell>Accept phased price reduction over 12 months</TableCell>
                          <TableCell>High</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Discount</TableCell>
                          <TableCell>Strong volume capability without volume incentives</TableCell>
                          <TableCell>Preferred status for Sliced Cheddar deserves volume rebates</TableCell>
                          <TableCell>Current pricing already factors in volume</TableCell>
                          <TableCell>Highlight multi-year commitment potential</TableCell>
                          <TableCell>Accept smaller discount tiers with guaranteed minimums</TableCell>
                          <TableCell>High</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Payment Terms</TableCell>
                          <TableCell>Current terms are Net 30</TableCell>
                          <TableCell>Extended terms align with industry standards</TableCell>
                          <TableCell>Extended terms increase financial burden</TableCell>
                          <TableCell>Offer early payment options with small discount</TableCell>
                          <TableCell>Accept Net 45 with volume commitment</TableCell>
                          <TableCell>Medium</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>
                
                {/* PremiumDairy */}
                <TabPanel value={selectedSupplierTab} index={2}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                    PremiumDairy Negotiation Strategy
                  </Typography>
                  
                  {/* Negotiation Targets Table */}
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Negotiation Targets
                  </Typography>
                  <TableContainer sx={{ mb: 4 }}>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '16%' }}>Negotiation Parameter</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Objective</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Opening Position</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>MDO</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>LAA</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>BATNA</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Price</TableCell>
                          <TableCell>Maintain premium quality with competitive pricing</TableCell>
                          <TableCell>5% reduction on Mascarpone for 60% allocation</TableCell>
                          <TableCell>3% reduction</TableCell>
                          <TableCell>2% reduction</TableCell>
                          <TableCell>Maintain current pricing</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Discount</TableCell>
                          <TableCell>Secure multi-year contract with KPIs</TableCell>
                          <TableCell>2% annual rebate with performance bonuses</TableCell>
                          <TableCell>1.5% rebate</TableCell>
                          <TableCell>1% rebate</TableCell>
                          <TableCell>Current terms</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Payment Terms</TableCell>
                          <TableCell>Extend payment window</TableCell>
                          <TableCell>Net 45 days</TableCell>
                          <TableCell>Net 40 days</TableCell>
                          <TableCell>Net 30 days</TableCell>
                          <TableCell>Current terms</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Incoterms</TableCell>
                          <TableCell>Maintain premium delivery service</TableCell>
                          <TableCell>DAP (Delivered At Place)</TableCell>
                          <TableCell>DAP with flexible delivery windows</TableCell>
                          <TableCell>Current terms</TableCell>
                          <TableCell>Current terms</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Negotiation Tactics Table */}
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Negotiation Tactics
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>Negotiation Parameter</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>Issue</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Key Message</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Anticipated Response</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Counter Response</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Concessions</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Priority</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Price</TableCell>
                          <TableCell>Premium pricing for premium quality</TableCell>
                          <TableCell>Increased volume allocation (60% for Mascarpone) justifies price reduction</TableCell>
                          <TableCell>Premium quality requires premium pricing</TableCell>
                          <TableCell>Multi-year commitment with guaranteed volumes offsets price reduction</TableCell>
                          <TableCell>Accept smaller reduction with performance KPIs</TableCell>
                          <TableCell>Medium</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Discount</TableCell>
                          <TableCell>No performance incentives in current contract</TableCell>
                          <TableCell>Performance KPIs with bonuses for meeting targets</TableCell>
                          <TableCell>Already delivering high performance</TableCell>
                          <TableCell>Formalize performance in contract with financial incentives</TableCell>
                          <TableCell>Accept higher performance thresholds</TableCell>
                          <TableCell>High</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Payment Terms</TableCell>
                          <TableCell>Current terms are Net 30</TableCell>
                          <TableCell>Extended terms align with industry standards</TableCell>
                          <TableCell>Premium service requires prompt payment</TableCell>
                          <TableCell>Offer early payment options for critical deliveries</TableCell>
                          <TableCell>Accept Net 40 with premium service guarantee</TableCell>
                          <TableCell>Low</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>
                
                {/* CheeseKing */}
                <TabPanel value={selectedSupplierTab} index={3}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                    CheeseKing Negotiation Strategy
                  </Typography>
                  
                  {/* Negotiation Targets Table */}
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Negotiation Targets
                  </Typography>
                  <TableContainer sx={{ mb: 4 }}>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '16%' }}>Negotiation Parameter</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Objective</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Opening Position</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>MDO</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>LAA</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>BATNA</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Price</TableCell>
                          <TableCell>Lock in competitive pricing with multi-year contract</TableCell>
                          <TableCell>Maintain current bid pricing with 3-year contract</TableCell>
                          <TableCell>Maintain pricing with 2-year contract</TableCell>
                          <TableCell>Accept 2% annual increase cap</TableCell>
                          <TableCell>Annual price negotiations</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Discount</TableCell>
                          <TableCell>Secure eco-incentives for sustainability credentials</TableCell>
                          <TableCell>2% additional discount for recycled packaging</TableCell>
                          <TableCell>1.5% discount</TableCell>
                          <TableCell>1% discount</TableCell>
                          <TableCell>Standard volume discounts</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Payment Terms</TableCell>
                          <TableCell>Establish favorable payment terms</TableCell>
                          <TableCell>Net 45 days</TableCell>
                          <TableCell>Net 40 days</TableCell>
                          <TableCell>Net 30 days</TableCell>
                          <TableCell>Industry standard terms</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Incoterms</TableCell>
                          <TableCell>Establish favorable delivery terms</TableCell>
                          <TableCell>DAP (Delivered At Place)</TableCell>
                          <TableCell>FCA (Free Carrier)</TableCell>
                          <TableCell>EXW (Ex Works)</TableCell>
                          <TableCell>Industry standard terms</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Negotiation Tactics Table */}
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    Negotiation Tactics
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>Negotiation Parameter</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>Issue</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Key Message</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Anticipated Response</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Counter Response</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Concessions</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Priority</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Price</TableCell>
                          <TableCell>New supplier relationship needs stability</TableCell>
                          <TableCell>Multi-year contract provides volume certainty and growth opportunity</TableCell>
                          <TableCell>Need flexibility for market fluctuations</TableCell>
                          <TableCell>Offer volume guarantees with minimal annual adjustments</TableCell>
                          <TableCell>Accept annual price review with caps</TableCell>
                          <TableCell>High</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Discount</TableCell>
                          <TableCell>Superior sustainability credentials</TableCell>
                          <TableCell>Eco-incentives align with corporate sustainability goals</TableCell>
                          <TableCell>Sustainability investments already reflected in pricing</TableCell>
                          <TableCell>Highlight marketing value of sustainability partnership</TableCell>
                          <TableCell>Accept smaller eco-incentive with marketing collaboration</TableCell>
                          <TableCell>Medium</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Payment Terms</TableCell>
                          <TableCell>New supplier relationship</TableCell>
                          <TableCell>Industry standard terms support new partnership</TableCell>
                          <TableCell>New relationship requires faster payment</TableCell>
                          <TableCell>Offer volume commitments to offset payment terms</TableCell>
                          <TableCell>Accept phased implementation of payment terms</TableCell>
                          <TableCell>Low</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Filters Section - Visible on all tabs */}
          <Grid item xs={12} sx={{ mt: 4 }}>
            <Paper sx={{ width: '100%', p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Filters
              </Typography>
              <Grid container spacing={3}>
                {/* Supplier Filter */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Filter by Supplier:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, maxHeight: '200px', overflowY: 'auto' }}>
                    {allSuppliers.map((supplier) => (
                      <Button
                        key={supplier}
                        variant={selectedSuppliers.includes(supplier) ? "contained" : "outlined"}
                        size="small"
                        onClick={() => {
                          if (selectedSuppliers.includes(supplier)) {
                            setSelectedSuppliers(selectedSuppliers.filter(s => s !== supplier));
                          } else {
                            setSelectedSuppliers([...selectedSuppliers, supplier]);
                          }
                        }}
                        sx={{
                          textTransform: 'none',
                          backgroundColor: selectedSuppliers.includes(supplier) ? '#8950FC' : 'transparent',
                          color: selectedSuppliers.includes(supplier) ? '#ffffff' : '#8950FC',
                          borderColor: '#8950FC',
                          '&:hover': {
                            backgroundColor: selectedSuppliers.includes(supplier) ? '#7339FB' : 'rgba(137, 80, 252, 0.04)',
                            borderColor: '#7339FB'
                          }
                        }}
                      >
                        {supplier}
                      </Button>
                    ))}
                  </Box>
                </Grid>
                
                {/* Category Filter */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Filter by Category:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, maxHeight: '200px', overflowY: 'auto' }}>
                    {allCategories.map((category) => (
                      <Button
                        key={category}
                        variant={selectedCategories.includes(category) ? "contained" : "outlined"}
                        size="small"
                        onClick={() => {
                          if (selectedCategories.includes(category)) {
                            setSelectedCategories(selectedCategories.filter(c => c !== category));
                          } else {
                            setSelectedCategories([...selectedCategories, category]);
                          }
                        }}
                        sx={{
                          textTransform: 'none',
                          backgroundColor: selectedCategories.includes(category) ? '#8950FC' : 'transparent',
                          color: selectedCategories.includes(category) ? '#ffffff' : '#8950FC',
                          borderColor: '#8950FC',
                          '&:hover': {
                            backgroundColor: selectedCategories.includes(category) ? '#7339FB' : 'rgba(137, 80, 252, 0.04)',
                            borderColor: '#7339FB'
                          }
                        }}
                      >
                        {category}
                      </Button>
                    ))}
                  </Box>
                </Grid>
                
                {/* Clear Filters Button */}
                <Grid item xs={12}>
                  {(selectedCategories.length > 0 || selectedSuppliers.length > 0) && (
                    <Button 
                      variant="outlined" 
                      onClick={handleClearFilters}
                      size="small"
                      sx={{ 
                        color: '#8950FC',
                        borderColor: '#8950FC',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#7339FB',
                          backgroundColor: 'rgba(137, 80, 252, 0.04)'
                        }
                      }}
                    >
                      Clear All Filters
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {/* Tabs Section */}
          <Grid item xs={12}>
            <Paper sx={{ width: '100%' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  aria-label="negotiation tabs"
                  sx={{
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#8950FC',
                    },
                    '& .Mui-selected': {
                      color: '#8950FC !important',
                    },
                  }}
                >
                  <Tab label="Status Quo" {...a11yProps(0)} />
                  <Tab label="Tender Summary" {...a11yProps(1)} />
                  <Tab label="Should-Cost" {...a11yProps(2)} />
                  <Tab label="Reasoning" {...a11yProps(3)} />
                  <Tab label="Actions" {...a11yProps(4)} />
                </Tabs>
              </Box>
              
              <TabPanel value={tabValue} index={0}>
                <Typography variant="h6" gutterBottom color="primary">
                  Supplier Spend Analysis
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This visualization shows the spend by category for each supplier over time.
                </Typography>
                
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : Object.keys(chartOptions).length > 0 ? (
                  <>
                    {/* 2024 Spend Overview Pie Charts */}
                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                      2024 Spend Overview
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      These charts show the breakdown of expected spend for 2024 by supplier, category, and main category.
                    </Typography>
                    
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      {Object.keys(pieChartOptions).length > 0 && (
                        <>
                          <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                              <HighchartsReact
                                highcharts={Highcharts}
                                options={pieChartOptions['supplier']}
                              />
                            </Paper>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                              <HighchartsReact
                                highcharts={Highcharts}
                                options={pieChartOptions['category']}
                              />
                            </Paper>
                          </Grid>
                        </>
                      )}
                    </Grid>
                    
                    {/* Supplier Spend Trends Line Charts */}
                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 4 }}>
                      Supplier Spend Trends
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      These charts show the spend trends by category for each supplier over time.
                    </Typography>
                    
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      {Object.keys(chartOptions).map((supplier) => (
                        <Grid item xs={12} key={supplier}>
                          <Paper sx={{ p: 2 }}>
                            <HighchartsReact
                              highcharts={Highcharts}
                              options={chartOptions[supplier]}
                            />
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                    
                    {/* Citation Source Link */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        <strong>Source:</strong>
                      </Typography>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={handleOpenCitation}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: '#8950FC',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        <DescriptionIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        Supplier Cost Data
                      </Link>
                    </Box>
                    
                    {/* Performance Concerns Section */}
                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 4 }}>
                      Performance Concerns
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      This table highlights potential performance issues and concerns with suppliers based on scorecards and stakeholder interviews.
                    </Typography>
                    
                    {loadingRedFlags ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <TableContainer component={Paper} sx={{ mb: 4 }}>
                        <Table sx={{ minWidth: 650 }} aria-label="performance concerns table">
                          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Supplier</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Criteria</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Reference</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {redFlagsData.length > 0 ? (
                              // Filter the red flags data based on selected suppliers
                              redFlagsData
                                .filter(flag => 
                                  // If no suppliers are selected, show all. Otherwise, only show selected suppliers
                                  selectedSuppliers.length === 0 || selectedSuppliers.includes(flag['Supplier name'])
                                )
                                .map((flag, index) => (
                                  <TableRow key={index} sx={{ 
                                    '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                                    backgroundColor: flag.Citation === 'Stakeholder Interview' ? 'rgba(255, 235, 235, 0.3)' : 'inherit'
                                  }}>
                                    <TableCell>{flag['Supplier name']}</TableCell>
                                    <TableCell>{flag.Criteria}</TableCell>
                                    <TableCell>{flag.Description}</TableCell>
                                    <TableCell>{flag.Citation}</TableCell>
                                    <TableCell>
                                      <Button
                                        variant="text"
                                        size="small"
                                        onClick={() => handleOpenRedFlagCitation(flag)}
                                        sx={{
                                          color: '#8950FC',
                                          textTransform: 'none',
                                          display: 'flex',
                                          alignItems: 'center',
                                          '&:hover': {
                                            backgroundColor: 'rgba(137, 80, 252, 0.04)'
                                          }
                                        }}
                                      >
                                        <DescriptionIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                        View Reference
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={5} align="center">
                                  No red flags data available
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    No spend data available. Please check your connection or try again later.
                  </Typography>
                )}
              </TabPanel>
              
              <TabPanel value={tabValue} index={3}>
                <Typography variant="h6" gutterBottom color="primary">
                  Procurement Analysis Reasoning
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This section provides detailed reasoning and analysis for the procurement negotiation.
                </Typography>
                
                {reasoningData.split('──────────────────────────────').map((section, index) => {
                  if (index === 0) return null; // Skip the introduction
                  
                  // Extract section title
                  const titleMatch = section.match(/^\s*\n\s*\n(\d+\. [^\n]+)/);
                  if (!titleMatch) return null;
                  
                  const sectionTitle = titleMatch[1];
                  const isExpanded = expandedSections[sectionTitle] || false;
                  
                  // Process the section content to avoid duplication
                  const processedContent = () => {
                    // Get content after the section title
                    const contentAfterTitle = section.substring(section.indexOf(sectionTitle) + sectionTitle.length);
                    
                    // Find the next section marker if it exists
                    const nextSectionIndex = contentAfterTitle.indexOf('──────────────────────────────');
                    
                    // Return only the content up to the next section marker or all content if no marker
                    return nextSectionIndex !== -1 
                      ? contentAfterTitle.substring(0, nextSectionIndex).trim()
                      : contentAfterTitle.trim();
                  };
                  
                  return (
                    <Box key={index} sx={{ mb: 3 }}>
                      {/* Foldable section header */}
                      <Box 
                        onClick={() => toggleSection(sectionTitle)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          p: 0.5,
                          borderRadius: 1,
                          bgcolor: 'rgba(137, 80, 252, 0.08)',
                          '&:hover': {
                            bgcolor: 'rgba(137, 80, 252, 0.12)'
                          }
                        }}
                      >
                        <Box sx={{ mr: 1, transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
                          ▶
                        </Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {sectionTitle}
                        </Typography>
                      </Box>
                      
                      {/* Section content (only shown when expanded) */}
                      {isExpanded && (
                        <Box sx={{ mt: 0, ml: 1, p: 0 }}>
                          {/* Format the content with bold subsection headers */}
                          {processedContent().split(/\n\s*\n([a-z]\. [^\n]+)/).map((part, idx) => {
                            if (idx === 0) {
                              // First part is just content before any subsection
                              return (
                                <Typography 
                                  key={`content-${idx}`}
                                  variant="body2" 
                                  component="pre" 
                                  sx={{ 
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.2,
                                    my: 0,
                                    py: 0,
                                    fontSize: '1rem',
                                    fontFamily: 'inherit'
                                  }}
                                >
                                  {part}
                                </Typography>
                              );
                            } else if (idx % 2 === 1) {
                              // Odd indices are subsection headers
                              return (
                                <Typography 
                                  key={`header-${idx}`}
                                  variant="subtitle2" 
                                  fontWeight="bold"
                                  sx={{ 
                                    mt: 1,
                                    mb: 0,
                                    fontSize: '1rem'
                                  }}
                                >
                                  {part}
                                </Typography>
                              );
                            } else {
                              // Even indices (except 0) are content after subsection headers
                              return (
                                <Typography 
                                  key={`content-${idx}`}
                                  variant="body2" 
                                  component="pre" 
                                  sx={{ 
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: 1.2,
                                    my: 0,
                                    py: 0,
                                    fontSize: '1rem',
                                    fontFamily: 'inherit'
                                  }}
                                >
                                  {part}
                                </Typography>
                              );
                            }
                          })}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </TabPanel>
              
              <TabPanel value={tabValue} index={1}>
                <Typography variant="h6" gutterBottom color="primary">
                  Supplier Bid Analysis
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This section shows detailed supplier bid comparisons for each product against the baseline price.
                </Typography>
                
                {loadingBids ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/* Product Bid Charts */}
                    {Object.keys(bidChartOptions).length > 0 ? (
                      <Grid container spacing={2} sx={{ mb: 4 }}>
                        {allProducts.map((product) => (
                          <Grid item xs={12} key={product}>
                            <Paper sx={{ p: 2 }}>
                              <HighchartsReact
                                highcharts={Highcharts}
                                options={bidChartOptions[product]}
                              />
                              <Box sx={{ mt: 1, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Note:</strong> Green bars indicate prices below or equal to baseline (better). Red bars indicate prices above baseline (worse). Labels show the percentage difference (+/-) from baseline price.
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        No supplier bid data available. Please check your connection or try again later.
                      </Typography>
                    )}
                    
                    {/* Tender Summary Table */}
                    <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 4 }}>
                      Tender Documents
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      This table displays the tender documents submitted by each supplier.
                    </Typography>
                    
                    {loadingTender ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <TableContainer component={Paper} sx={{ mb: 4 }}>
                        <Table sx={{ minWidth: 650 }} aria-label="tender summary table">
                          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Supplier</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Bid Sheet</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Summary</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tenderData.length > 0 ? (
                              tenderData.map((row, index) => (
                                <TableRow key={row.file_id} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                                  <TableCell>{row.supplier_name}</TableCell>
                                  <TableCell>{row.file_name}</TableCell>
                                  <TableCell>
                                    <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
                                      <ReactMarkdown>{row.summary}</ReactMarkdown>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3} align="center">
                                  No tender summary data available
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}
              </TabPanel>
              
              <TabPanel value={tabValue} index={2}>
                <Typography variant="h6" gutterBottom color="primary">
                  Should-Cost Modeling
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Historical price comparison by category and supplier. These charts show the price trends over time for each product category, allowing for should-cost analysis and future price projections.
                </Typography>
                
                {loadingHistoricalData ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                      Loading historical data...
                    </Typography>
                  </Box>
                ) : Object.keys(historicalChartOptions).length > 0 ? (
                  <Grid container spacing={3}>
                    {Object.keys(historicalChartOptions).map(product => (
                      <Grid item xs={12} key={product}>
                        <Paper sx={{ p: 2, mb: 3 }}>
                          <HighchartsReact
                            highcharts={Highcharts}
                            options={historicalChartOptions[product]}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                            This chart shows the price evolution for the {product} category across different suppliers over time. 
                            The data includes both historical prices and bid prices for future years.
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      No historical data available for should-cost modeling.
                    </Typography>
                  </Box>
                )}
              </TabPanel>
              
              <TabPanel value={tabValue} index={4}>
                <Typography variant="h6" gutterBottom color="primary">
                  RFP Deviations
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This section outlines the key quality issues that need to be addressed for each supplier.
                </Typography>
                
                {/* Quality Issues Table */}
                <Paper sx={{ p: 2, mb: 4 }}>
                  <Box sx={{ mt: 2 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Supplier</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: '80%' }}>Quality Issue</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>DAIRYLICIOUS</TableCell>
                            <TableCell>
                              Missing compliance documentation for salt content and EU sourcing. The analysis shows "nan" (missing) compliance data in these areas, which presents a quality assurance risk and potential regulatory compliance issue.
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>DairyPro</TableCell>
                            <TableCell>
                              Fat content in Sliced Cheddar products is at the upper limit of acceptable range (32.1% vs. maximum 33%). Need to request quality improvement plan to ensure consistent fat content within target range and prevent potential quality variations.
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>PremiumDairy</TableCell>
                            <TableCell>
                              Product specification sheets for Mascarpone show inconsistent quality metrics between batches. Need to address quality control processes to ensure consistent product characteristics and texture across all deliveries.
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>CheeseKing</TableCell>
                            <TableCell>
                              Verification needed for animal welfare compliance claims and recycled/biodegradable packaging certification. While documentation has been provided, third-party verification and on-site audit is required to confirm compliance with all sustainability standards.
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>All Suppliers</TableCell>
                            <TableCell>
                              Standardization of quality testing methodologies across all suppliers is needed. Current inconsistencies in testing protocols make direct quality comparisons difficult. Implement unified quality control standards and reporting formats to ensure consistent product quality across all suppliers.
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Paper>
              </TabPanel>
            </Paper>
          </Grid>
        </Box>
      </Grid>

      {/* Chat Bubble */}
      <Tooltip title="Chat with Negotiation Assistant">
        <Fab 
          color="primary" 
          aria-label="chat"
          onClick={handleOpenSidebar}
          sx={{ 
            position: 'fixed', 
            bottom: 40, 
            right: 20,
            zIndex: 1000,
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)'
          }}
        >
          <ChatIcon />
        </Fab>
      </Tooltip>

      {/* Sidebar Chat Component */}
      <Sidebar isOpen={isSidebarOpen} onClose={handleCloseSidebar} />
      
      {/* Citation Panel */}
      {selectedReference && (
        <CitationPanel
          open={isCitationOpen}
          onClose={handleCloseCitation}
          selectedReference={selectedReference}
        />
      )}
    </Box>
  );
};

export default Negotiations;
